import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormError,
  FormRadioGroup,
  FormSubmitButton,
  freeTextPrefixForOption,
} from '@pm/forms';
import { SingleSelect, Text, TextLink } from '@pm/ui';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useAppointmentCancellationReasons } from '../../../hooks/use-appointment-cancellation-reasons/use-appointment-cancellation-reasons';
import { toServerError } from '../../../utilities/errors';
import { SubmitProps } from './cancel.types';

type CancelAppointmentFormProps = {
  supportLink: string;
  onSubmit?: (props: SubmitProps) => Promise<void>;
};
export const CancelAppointmentForm = ({
  supportLink,
  onSubmit,
}: CancelAppointmentFormProps) => {
  const { t } = useTranslation(['appointments', 'encounters', 'global']);
  const {
    reasons: cancellationReasons,
    freeTextReasons,
    freeTextDelimiter,
  } = useAppointmentCancellationReasons({});

  const schema = z
    .object({
      reason: z.string().min(1, t('global:Required')),
    })
    .refine(
      ({ reason }) => {
        if (reason === '') {
          return false;
        }

        const hasEmptyFreeText = freeTextReasons.some(
          (option) =>
            reason.trim() ===
            freeTextPrefixForOption({ option, freeTextDelimiter }).trim(),
        );
        return !hasEmptyFreeText;
      },
      {
        message: t('global:Required'),
        path: ['reason'],
      },
    );

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
    mode: 'all',
  });

  const handleSubmit: SubmitHandler<z.infer<typeof schema>> = async ({
    reason,
  }) => {
    await onSubmit?.({
      reason,
      onError: () =>
        methods.setError(...toServerError('Unable to cancel appointment')),
    });
  };

  const cancelKey = 'appointments:ManageAppointment.Cancel';

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <div className="space-y-xxxl">
          <div className="space-y-l">
            <FormError
              error={{
                title: t('encounters:Errors.Cancel.Title'),
                instructions: (
                  <Trans
                    ns="global"
                    i18nKey="Errors.ContactSupport.Instructions"
                  >
                    <TextLink to={supportLink} external />
                  </Trans>
                ),
              }}
            />
            <Text as="h1" size="headingS" id="cancellation-reason-label">
              {t(`${cancelKey}.Heading`)}
            </Text>
            <FormRadioGroup
              name="reason"
              aria-labelledby="cancellation-reason-label"
              space="m"
              options={cancellationReasons}
              freeTextDelimiter={freeTextDelimiter}
              RadioComponent={SingleSelect}
            />
          </div>
          <FormSubmitButton width="full">
            {t('appointments:ManageAppointment.Cancel.Actions.Confirm')}
          </FormSubmitButton>
        </div>
      </form>
    </FormProvider>
  );
};
